import { API_BASE_URL } from './api';

function authHeaders() {
  const token = localStorage.getItem('doorbase_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function safeFilename(value = 'document') {
  return String(value)
    .trim()
    .replace(/[^a-z0-9-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'document';
}

const GENERATED_DOCUMENT_CACHE_TTL_MS = 2 * 60 * 1000;
const GENERATED_DOCUMENT_CACHE_LIMIT = 8;
const DOCUMENT_GENERATION_TIMEOUT_MS = 90000;
const DOCUMENT_GENERATION_RETRY_DELAY_MS = 1200;
const generatedDocumentCache = new Map();

function pruneGeneratedDocumentCache() {
  const now = Date.now();
  for (const [key, entry] of generatedDocumentCache.entries()) {
    if (now - entry.createdAt > GENERATED_DOCUMENT_CACHE_TTL_MS) {
      generatedDocumentCache.delete(key);
    }
  }

  while (generatedDocumentCache.size > GENERATED_DOCUMENT_CACHE_LIMIT) {
    const oldestKey = generatedDocumentCache.keys().next().value;
    generatedDocumentCache.delete(oldestKey);
  }
}

function cacheKeyForDocument(template, data, outputFormat) {
  return JSON.stringify({
    templateId: template?.id,
    outputFormat,
    data,
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientFetchError(error) {
  return error?.name === 'AbortError'
    || /Failed to fetch|NetworkError|Load failed|fetch/i.test(error?.message || '');
}

async function fetchGeneratedDocument(template, data, outputFormat) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), DOCUMENT_GENERATION_TIMEOUT_MS);

  try {
    return await fetch(`${API_BASE_URL}/api/documents/generate`, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_id: template.id,
        data,
        output_format: outputFormat,
      }),
      signal: controller.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('The document service took too long to respond. Please try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function fetchGeneratedDocumentWithRetry(template, data, outputFormat) {
  try {
    return await fetchGeneratedDocument(template, data, outputFormat);
  } catch (error) {
    if (!isTransientFetchError(error)) {
      throw error;
    }

    await sleep(DOCUMENT_GENERATION_RETRY_DELAY_MS);

    try {
      return await fetchGeneratedDocument(template, data, outputFormat);
    } catch (retryError) {
      if (isTransientFetchError(retryError)) {
        throw new Error('Could not reach the document service. Please wait a moment and try again.');
      }
      throw retryError;
    }
  }
}

async function requestGeneratedDocument(template, data, outputFormat) {
  if (!template?.id) {
    throw new Error('Please select a Word document template first.');
  }

  pruneGeneratedDocumentCache();
  const cacheKey = cacheKeyForDocument(template, data, outputFormat);
  const cached = generatedDocumentCache.get(cacheKey);
  if (cached && Date.now() - cached.createdAt <= GENERATED_DOCUMENT_CACHE_TTL_MS) {
    return cached.promise;
  }

  const promise = (async () => {
    const response = await fetchGeneratedDocumentWithRetry(template, data, outputFormat);

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let error = {};
      try {
        error = text ? JSON.parse(text) : {};
      } catch {
        error = {};
      }
      throw new Error(error.error || text || `Document generation failed (${response.status})`);
    }

    const blob = await response.blob();
    if (!blob || blob.size === 0) {
      throw new Error('Document generation returned an empty file');
    }
    return blob;
  })();

  generatedDocumentCache.set(cacheKey, { promise, createdAt: Date.now() });
  try {
    return await promise;
  } catch (error) {
    generatedDocumentCache.delete(cacheKey);
    throw error;
  }
}

function blobToDataUri(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read generated PDF'));
    reader.readAsDataURL(blob);
  });
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getTargetWindow(existingWindow = null) {
  if (existingWindow && !existingWindow.closed) {
    return existingWindow;
  }
  return window.open('', '_blank');
}

function printBlob(blob, filename, printWindow = null) {
  const url = window.URL.createObjectURL(blob);
  const targetWindow = getTargetWindow(printWindow);

  if (!targetWindow) {
    downloadBlob(blob, filename);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return;
  }

  targetWindow.document.open();
  targetWindow.document.write(`<!doctype html>
<html>
  <head>
    <title>${escapeHtml(filename)}</title>
    <style>
      html, body { margin: 0; height: 100%; background: #f3f4f6; font-family: Arial, sans-serif; }
      .bar { height: 44px; display: flex; align-items: center; gap: 8px; padding: 0 12px; background: #111827; color: #fff; }
      button { border: 0; border-radius: 4px; padding: 7px 12px; background: #fff; color: #111827; cursor: pointer; }
      iframe { width: 100%; height: calc(100vh - 44px); border: 0; background: #fff; }
    </style>
  </head>
  <body>
    <div class="bar">
      <button onclick="document.getElementById('doc').contentWindow.print()">Print</button>
      <span>${escapeHtml(filename)}</span>
    </div>
    <iframe id="doc" src="${url}" onload="setTimeout(() => this.contentWindow.print(), 300)"></iframe>
  </body>
</html>`);
  targetWindow.document.close();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 10 * 60 * 1000);
}

function previewBlob(blob, filename, previewWindow = null) {
  const url = window.URL.createObjectURL(blob);
  const targetWindow = getTargetWindow(previewWindow);

  if (!targetWindow) {
    downloadBlob(blob, filename);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return;
  }

  targetWindow.location.href = url;
  window.setTimeout(() => window.URL.revokeObjectURL(url), 10 * 60 * 1000);
}

export async function generateDocumentOutput(template, action, data, filenameBase, outputWindow = null) {
  const outputFormat = action === 'docx' ? 'docx' : 'pdf';
  const blob = await requestGeneratedDocument(template, data, outputFormat);
  const base = safeFilename(filenameBase || template.name);
  const filename = `${base}.${outputFormat}`;

  if (action === 'print') {
    printBlob(blob, filename, outputWindow);
    return;
  }

  if (action === 'preview') {
    previewBlob(blob, filename, outputWindow);
    return;
  }

  if (action === 'email') {
    throw new Error('Email sending is not available for this document type yet');
  }

  downloadBlob(blob, filename);
}

export async function previewDocumentOutput(template, data, filenameBase, previewWindow = null) {
  const blob = await requestGeneratedDocument(template, data, 'pdf');
  const base = safeFilename(filenameBase || template.name);
  previewBlob(blob, `${base}.pdf`, previewWindow);
}

export async function generateDocumentPdfDataUri(template, data) {
  const blob = await requestGeneratedDocument(template, data, 'pdf');
  return blobToDataUri(blob);
}
